/**
 * How money reaches the workshop (#2467).
 *
 * Pricing answers "what do we charge"; this answers "how do we get paid".
 * They were interleaved before: the IBAN sat with the address, the VAT number
 * with the hourly rate, and payment terms were the last thing on the page,
 * three sections below the interest rate they belong with.
 *
 * The VAT *number* is here because it identifies the invoice. The VAT *rates*
 * stay in pricing because they are an input to price calculation.
 *
 * Payment terms are not part of `WorkshopSettings` — they live in the page's
 * own state and are passed through, which is why this section takes two extra
 * props rather than reading them off `settings`.
 */

import { Paper, Typography, TextField, Grid, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings } from '@/types';
import { normalizeBic, normalizeIban } from '@/utils/iban';
import { PaymentTermsSettings } from '@/features/settings/PaymentTermsSettings';

interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
  paymentTermsDaysOptions: number[];
  onPaymentTermsChange: (value: number[]) => void;
}

export function WorkshopSectionBilling({
  settings,
  onChange,
  paymentTermsDaysOptions,
  onPaymentTermsChange,
}: Props) {
  const { t } = useTranslation('settings');

  const handleChange = (field: keyof WorkshopSettings, value: string | number) => {
    onChange({ ...settings, [field]: value });
  };

  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('billingSection.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('billingSection.subtitle')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Grid container spacing={3}>
          {/*
            The IBAN blocks invoice finalization (finalize-draft-invoice.ts)
            but had no editor outside the first-run wizard, so a workshop that
            skipped it there could neither invoice nor fix the gap (#2461).
            Normalized on blur rather than on every keystroke, so typing a
            grouped IBAN stays comfortable.
          */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label={t('bankIban')}
              value={settings.bankIban ?? ''}
              onChange={(e) => handleChange('bankIban', e.target.value)}
              onBlur={(e) => handleChange('bankIban', normalizeIban(e.target.value))}
              helperText={t('bankIbanHint')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('bankBic')}
              value={settings.bankBic ?? ''}
              onChange={(e) => handleChange('bankBic', e.target.value)}
              onBlur={(e) => handleChange('bankBic', normalizeBic(e.target.value))}
              helperText={t('bankBicHint')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('vatNumber')}
              value={settings.vatNumber || ''}
              onChange={(e) => handleChange('vatNumber', e.target.value)}
              helperText={t('vatNumberHint')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('lateInterestRatePercent')}
              value={settings.lateInterestRatePercent ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onChange({ ...settings, lateInterestRatePercent: undefined });
                  return;
                }
                const val = parseFloat(raw);
                if (!Number.isNaN(val)) {
                  onChange({ ...settings, lateInterestRatePercent: val });
                }
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: { min: 0, max: 100, step: '0.1' },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('lateInterestRatePercentConsumer')}
              value={settings.lateInterestRatePercentConsumer ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onChange({ ...settings, lateInterestRatePercentConsumer: undefined });
                  return;
                }
                const val = parseFloat(raw);
                if (!Number.isNaN(val)) {
                  onChange({ ...settings, lateInterestRatePercentConsumer: val });
                }
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: { min: 0, max: 100, step: '0.1' },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">
              {t('lateInterestRatePercentHint')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <PaymentTermsSettings value={paymentTermsDaysOptions} onChange={onPaymentTermsChange} />
    </>
  );
}
